import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiService } from "../services/api";
import { getApiErrorMessage } from "../lib/apiErrors";
import type { ParticipantResponse, EligibleParticipantResponse } from "../types/tickets";

const participantsQueryKey = (ticketId: string) => ['customer-portal', 'tickets', ticketId, 'participants'] as const;

/**
 * Hook to get participants for a ticket in customer portal
 */
export function useCustomerPortalParticipants(ticketId: string | undefined) {
  return useQuery({
    queryKey: participantsQueryKey(ticketId!),
    queryFn: async () => {
      const response = await apiService.getCustomerPortalTicketParticipants(ticketId!);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch participants'));
      }
      return response.data as ParticipantResponse[];
    },
    enabled: !!ticketId,
    staleTime: 30000,
  });
}

/**
 * Whether the text is a complete email address. The portal only looks contacts up by exact email - customers can't
 * browse or search the organization's other customers - so there's nothing to query until the address is complete.
 */
export function isCompleteEmailAddress(value?: string): value is string {
  return !!value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/**
 * Hook to look up an eligible participant for a ticket in customer portal, by exact email
 */
export function useCustomerPortalEligibleParticipants(ticketId: string | undefined, search?: string) {
  return useQuery({
    queryKey: [...participantsQueryKey(ticketId!), 'eligible', search],
    queryFn: async () => {
      const response = await apiService.getCustomerPortalEligibleParticipants(ticketId!, search);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch eligible participants'));
      }
      return response.data as EligibleParticipantResponse[];
    },
    enabled: !!ticketId && isCompleteEmailAddress(search),
    staleTime: 10000,
  });
}

/**
 * Hook to look up an eligible participant, by exact email, for creating a new ticket in customer portal
 * (before the ticket exists)
 */
export function useCustomerPortalEligibleParticipantsForNewTicket(search?: string) {
  return useQuery({
    queryKey: ['customer-portal', 'participants', 'new-ticket', 'eligible', search],
    queryFn: async () => {
      const response = await apiService.getCustomerPortalEligibleParticipantsForNewTicket(search);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch eligible participants'));
      }
      return response.data as EligibleParticipantResponse[];
    },
    enabled: isCompleteEmailAddress(search),
    staleTime: 10000,
  });
}

/**
 * Hook to add a participant to a ticket in customer portal
 */
export function useAddCustomerPortalParticipant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, customerId }: { ticketId: string; customerId: string }) => {
      const response = await apiService.addCustomerPortalTicketParticipant(ticketId, customerId);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to add participant'));
      }
      return response.data;
    },
    onSuccess: (_, { ticketId }) => {
      // Invalidate participants query
      queryClient.invalidateQueries({
        queryKey: participantsQueryKey(ticketId),
      });
    },
  });
}

/**
 * Hook to remove a participant from a ticket in customer portal
 */
export function useRemoveCustomerPortalParticipant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, customerId }: { ticketId: string; customerId: string }) => {
      const response = await apiService.removeCustomerPortalTicketParticipant(ticketId, customerId);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to remove participant'));
      }
      return response.data;
    },
    onSuccess: (_, { ticketId }) => {
      // Invalidate participants query
      queryClient.invalidateQueries({
        queryKey: participantsQueryKey(ticketId),
      });
    },
  });
}
